/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useCallback } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator, Dimensions, Platform } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { createApiClient, apiClient,getLocalTimestamp } from "../../../api/apiClient";
import { useToast } from "../../../components/LhToastContainer";
import {
  setLHExecutionData,
  setLHExecutionLoading,
  setLHExecutionError,
} from "../../../store/slices/homescreenSlice";
import lightStyles, { createHomeScreenThemeStyles } from "../HomeScreen.style";
import MetricsChart from "./MetricsChart";

// Import text constants
import { TEXT } from "../../../constants/texts";
import { SCORE_THRESHOLDS, COLORS } from "../../../constants/themes";
import { FontFamily } from "../../../constants/fonts";
import CrossPlatformDropdown from "../../../components/CrossPlatformDropdown";
import { useTheme } from "../../../theme";

import { reportError } from "../../../utils/Instrumentation";

// Constants
const TIME_FRAME_OPTIONS = [
  { id: "CURRENT", displayText: "CW" },
  { id: "LCW", displayText: "LCW" },
  { id: "LCP", displayText: "LCP" },
  { id: "YTD", displayText: "YTD" },
];

const SCORE_COLORS = {
  HIGH: COLORS.SUCCESS, // Green
  MEDIUM: COLORS.WARNING, // Yellow
  LOW: COLORS.ERROR, // Red
};

// Types
interface MetricsSectionProps {
  selectedTimeFrame: string | null;
  setSelectedTimeFrame: (timeFrame: string | null) => void;
  metricsDirty: boolean;
  setMetricsDirty: (isDirty: boolean) => void;
  lastLoadedTimeFrame: string | null;
  setLastLoadedTimeFrame: (timeFrame: string | null) => void;
  setMetricsOverlayVisible: (isVisible: boolean) => void;
}

interface MetricCardProps {
  name: string;
  value: string;
  onPress: () => void;
}

interface LHExecutionDataItem {
  year: number;
  period: number;
  week?: number;
  leadershipWalksPercentage: number;
  pulseChecksPercentage: number;
  shiftHandoversPercentage: number;
  sparcWalksPercentage: number;
  observationsPercentage: number;
  lhexecutionPercentage: number;
}

interface LocationLHExecutionData extends LHExecutionDataItem {
  locationId: string;
  locationName: string;
}

interface RegionLHExecutionData extends LHExecutionDataItem {
  regionName: string;
  locationCount: number;
}

interface DivisionLHExecutionData extends LHExecutionDataItem {
  divisionName: string;
  locationCount: number;
}

interface LHExecutionData {
  timeframe: string;
  year: number;
  period: number;
  week?: number;
  isYearToDate: boolean;
  location: LocationLHExecutionData;
  region: RegionLHExecutionData;
  division: DivisionLHExecutionData;
}

// Metric Card Component
const MetricCard: React.FC<MetricCardProps> = ({ name, value, onPress }) => {
  const getColorForValue = (valueStr: string): string => {
    const numericValue = parseFloat(valueStr.replace("%", ""));
    return numericValue >= SCORE_THRESHOLDS.HIGH
      ? SCORE_COLORS.HIGH
      : numericValue >= SCORE_THRESHOLDS.MEDIUM
      ? SCORE_COLORS.MEDIUM
      : SCORE_COLORS.LOW;
  };

  return (
    <TouchableOpacity
      style={{
        flex: 1,
        alignItems: "center",
        marginHorizontal: 4,
      }}
      onPress={onPress}
    >
      <Text
        style={{
          fontSize: 12,
          color: "#333",
          marginBottom: 4,
          fontWeight: "500",
        }}
      >
        {name}
      </Text>
      <View
        style={{
          height: 58,
          width: "100%",
          borderRadius: 8,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: getColorForValue(value),
        }}
      >
        <Text
          style={{
            color: "white",
            fontWeight: "bold",
            fontSize: 16,
          }}
        >
          {value}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const MetricsSection: React.FC<MetricsSectionProps> = ({
  selectedTimeFrame,
  setSelectedTimeFrame,
  metricsDirty,
  setMetricsDirty,
  lastLoadedTimeFrame,
  setLastLoadedTimeFrame,
  setMetricsOverlayVisible,
}) => {
  const dispatch = useDispatch();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors, isDark), [colors, isDark]);
  const toast = useToast();
  const { selectedLocation } = useSelector((state: any) => state.appData);
  const { loading, lhExecutionData, lhExecutionCache, error } = useSelector(
    (state: any) => state.homescreen
  );

  // Tracks live window width (was a module-level constant computed once at
  // import time, so rotating an iPhone left the chart sized for the stale
  // portrait width and its axis labels rendered cramped/overlapping).
  const [screenWidth, setScreenWidth] = React.useState(Dimensions.get("window").width);
  React.useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription.remove();
  }, []);

  // Store the previous location ID to detect changes
  const prevLocationIdRef = React.useRef<string | null>(null);

  // Reset timeframe to 'CURRENT' when location changes
  useEffect(() => {
    if (selectedLocation && prevLocationIdRef.current !== selectedLocation.id) {
      // Only reset if the location has actually changed
      if (prevLocationIdRef.current !== null) {
        console.log("Location changed, resetting timeframe to CURRENT");
        setSelectedTimeFrame("CURRENT");
        setLastLoadedTimeFrame("CURRENT");
      }
      prevLocationIdRef.current = selectedLocation.id;
    }
  }, [selectedLocation, setSelectedTimeFrame, setLastLoadedTimeFrame]);

  // Track API calls in progress by timeframe to prevent duplicate calls
  const pendingApiCallsRef = React.useRef<Record<string, boolean>>({});

  // Keep track of failed timeframes to prevent retrying failed API calls
  const failedTimeframesRef = React.useRef<Record<string, boolean>>({});

  // Function to fetch LH Execution data with selected timeframe
  const fetchLHExecutionData = useCallback(
    async (timeFrame: string) => {
      if (!selectedLocation) {
        return;
      }

      const locationId = selectedLocation.id;
      const cacheKey = `${locationId}:${timeFrame}`;

      // Check if this timeframe previously failed - if so, don't retry automatically
      if (failedTimeframesRef.current[cacheKey]) {
        console.log(`Skipping API call that previously failed for ${cacheKey}`);
        setMetricsDirty(false);
        return;
      }

      // Skip API call if the values haven't actually changed
      if (timeFrame === lastLoadedTimeFrame) {
        console.log(
          "Skipping LH Execution API call - timeframe unchanged:",
          timeFrame
        );
        setMetricsDirty(false);
        return;
      }

      // Prevent duplicate API calls for the same timeframe
      if (pendingApiCallsRef.current[cacheKey]) {
        console.log(
          `API call already in progress for ${cacheKey}, skipping duplicate request`
        );
        return;
      }

      // Check if we have cached data for this location and timeframe
      // Only use cache if there's no error
      if (
        lhExecutionCache &&
        lhExecutionCache[cacheKey] &&
        !error?.lhExecution
      ) {
        console.log(`Using cached LH Execution data for ${cacheKey}`);
        // Use cached data directly without making an API call
        dispatch(setLHExecutionData(lhExecutionCache[cacheKey]));
        setLastLoadedTimeFrame(timeFrame);
        setMetricsDirty(false);
        return;
      }

      // Check if we already have data for this timeframe and location in current data
      // Only use current data if there's no error
      const hasDataForTimeframeAndLocation =
        lhExecutionData &&
        !error?.lhExecution &&
        lhExecutionData.timeframe === timeFrame &&
        lhExecutionData.location?.locationId === selectedLocation.id;

      if (hasDataForTimeframeAndLocation) {
        console.log(
          "Using current LH Execution data for timeframe:",
          timeFrame
        );
        setLastLoadedTimeFrame(timeFrame);
        setMetricsDirty(false);
        return;
      }

      // Reset data before making API call
      dispatch(setLHExecutionData(null));
      dispatch(setLHExecutionLoading(true));

      // Mark this API call as in progress
      pendingApiCallsRef.current[cacheKey] = true;

      try {
        const api = createApiClient();
        const endpoint = `/home/lh-execution?location_name=${encodeURIComponent(
          selectedLocation.displayText || selectedLocation.name
        )}&timeframe=${timeFrame}`;
        console.log(`Fetching LH Execution data for ${cacheKey}`);
        const response = await apiClient(api, endpoint, "GET");
        // Add timestamp to the response to track when it was fetched
        response._fetchedAt = getLocalTimestamp()
        dispatch(setLHExecutionData(response));
        setLastLoadedTimeFrame(timeFrame);
        setMetricsDirty(false);
        console.log(`LH Execution data fetched and cached for ${cacheKey}`);
      } catch (err: any) {
        console.error("Error fetching LH Execution data:", err);
        reportError({
          severity: "ERROR",
          message: err instanceof Error ? err.message : String(err),
          stackTrace: err instanceof Error ? err.stack : undefined,
        });

        // Clear any previous data to make sure we don't show old data
        // when an API error occurs
        dispatch(setLHExecutionData(null));

        // Construct error message with status code if available
        let errorMessage = err.message || "Unknown error";
        if (err.response && err.response.status) {
          // Include the status code in the error message
          errorMessage = `${err.response.status}: ${errorMessage}`;
        }

        // Mark this timeframe as failed to prevent automatic retries
        failedTimeframesRef.current[cacheKey] = true;

        dispatch(setLHExecutionError(errorMessage));

        // Reset dirty flag to prevent repeated API calls after failure
        setMetricsDirty(false);

        // Set lastLoadedTimeFrame to current timeframe to prevent further attempts
        setLastLoadedTimeFrame(timeFrame);

        // Show error toast notification only once
        // if (toast) {
        //   // Custom message for timeout errors
        //   console.log('err:', err)
        //   const isTimeoutError = err.response?.status === 500 ||
        //                         errorMessage.includes('timeout') ||
        //                         errorMessage.includes('timed out');

        //   toast.showToast({
        //     header: 'Error Loading Metrics',
        //     subtext: isTimeoutError
        //       ? `Unable to load lighthouse metrics, request timed out. Please try again later.`
        //       : `Failed to load lighthouse metrics for timeframe: ${timeFrame}. Please try again later.`,
        //     variant: 'error'
        //   });
        // }
      } finally {
        // Mark API call as complete regardless of success or failure
        dispatch(setLHExecutionLoading(false));
        pendingApiCallsRef.current[cacheKey] = false;
      }
    },
    [
      selectedLocation,
      lastLoadedTimeFrame,
      setLastLoadedTimeFrame,
      setMetricsDirty,
      dispatch,
      lhExecutionData,
      lhExecutionCache,
    ]
  );

  // Effect to fetch LH Execution data when timeframe changes
  useEffect(() => {
    // Only proceed if we have basic requirements met
    if (
      !selectedTimeFrame ||
      !selectedLocation ||
      !metricsDirty ||
      loading.lhExecution
    ) {
      return;
    }

    const cacheKey = `${selectedLocation.id}:${selectedTimeFrame}`;

    // Check multiple conditions that would prevent API calls:

    // 1. Do we already have an error for this exact timeframe?
    const hasErrorForCurrentTimeFrame =
      error?.lhExecution && selectedTimeFrame === lastLoadedTimeFrame;

    // 2. Is this a timeframe that previously failed?
    const hasFailedPreviously = failedTimeframesRef.current[cacheKey];

    // If either condition is true, prevent API call
    if (hasErrorForCurrentTimeFrame || hasFailedPreviously) {
      console.log(
        `Preventing automatic API retry for timeframe: ${selectedTimeFrame}`
      );
      // Turn off dirty flag to prevent repeated attempts
      setMetricsDirty(false);
      return;
    }

    // Only fetch if no error conditions are met
    fetchLHExecutionData(selectedTimeFrame);
  }, [
    selectedTimeFrame,
    selectedLocation,
    metricsDirty,
    fetchLHExecutionData,
    loading.lhExecution,
    error?.lhExecution,
    lastLoadedTimeFrame,
    setMetricsDirty,
  ]);

  // Get formatted metrics data for display
  const formattedMetrics = useMemo(() => {
    if (loading.lhExecution) {
      return {
        location: { name: "Location", value: "0%" },
        region: { name: "Region", value: "0%" },
        division: { name: "Market", value: "0%" },
      };
    }

    // Return default values if there's an error or no data
    if (!lhExecutionData || error?.lhExecution) {
      return {
        location: { name: "Location", value: "0%" },
        region: { name: "Region", value: "0%" },
        division: { name: "Market", value: "0%" },
      };
    }
    return {
      location: {
        name: lhExecutionData.location?.locationName || "Location",
        value:
          typeof lhExecutionData.location?.lhexecutionPercentage === "number"
            ? `${Math.round(
                lhExecutionData.location.lhexecutionPercentage * 100
              )}%`
            : "0%",
      },
      region: {
        name: lhExecutionData.region?.regionName || "Region",
        value:
          typeof lhExecutionData.region?.lhexecutionPercentage === "number"
            ? `${Math.round(
                lhExecutionData.region.lhexecutionPercentage * 100
              )}%`
            : "0%",
      },
      market: {
        name: lhExecutionData.market?.marketName || "Market",
        value:
          typeof lhExecutionData.market?.lhexecutionPercentage === "number"
            ? `${Math.round(
                lhExecutionData.market.lhexecutionPercentage * 100
              )}%`
            : "0%",
      },
    };
  }, [lhExecutionData, loading.lhExecution]);

  // Transform lhExecutionData to chart data array for MetricsChart
  const metricsChartData = useMemo(() => {
    // Return empty array if there's an error or no data
    if (!lhExecutionData || error?.lhExecution) {
      return [];
    }

    const chartData = [];

    // Add location data
    if (lhExecutionData.location?.locationName && typeof lhExecutionData.location?.lhexecutionPercentage === 'number') {
      chartData.push({
        name: lhExecutionData.location.locationName,
        value: Math.round(lhExecutionData.location.lhexecutionPercentage * 100),
      });
    }

    // Add market data (Pacific Northwest - middle)
    if (lhExecutionData.market?.marketName && typeof lhExecutionData.market?.lhexecutionPercentage === 'number') {
      chartData.push({
        name: lhExecutionData.market.marketName,
        value: Math.round(lhExecutionData.market.lhexecutionPercentage * 100),
      });
    }

    // Add region data (West - top)
    if (lhExecutionData.region?.regionName && typeof lhExecutionData.region?.lhexecutionPercentage === 'number') {
      chartData.push({
        name: lhExecutionData.region.regionName,
        value: Math.round(lhExecutionData.region.lhexecutionPercentage * 100),
      });
    }

    return chartData;
  }, [lhExecutionData, error?.lhExecution]);

  // Transform lhExecutionData to metricsData array for overlay
  const metricsOverlayData = useMemo(() => {
    // Return empty array if there's an error or no data
    if (!lhExecutionData || error?.lhExecution) {
      return [];
    }

    const toPercent = (val: number | undefined) =>
      typeof val === "number" ? `${(val * 100).toFixed(2)}%` : "0%";
    return [
      {
        region: lhExecutionData.location?.locationName || "Location",
        pulseCheck: toPercent(lhExecutionData.location?.pulseChecksPercentage),
        observation: toPercent(
          lhExecutionData.location?.observationsPercentage
        ),
        shift: toPercent(lhExecutionData.location?.shiftHandoversPercentage),
        leadershipWalk: toPercent(
          lhExecutionData.location?.leadershipWalksPercentage
        ),
        sparcWalk: toPercent(lhExecutionData.location?.sparcWalksPercentage),
      },
      {
        region: lhExecutionData.market?.marketName || "Market",
        pulseCheck: toPercent(lhExecutionData.market?.pulseChecksPercentage),
        observation: toPercent(
          lhExecutionData.market?.observationsPercentage
        ),
        shift: toPercent(lhExecutionData.market?.shiftHandoversPercentage),
        leadershipWalk: toPercent(
          lhExecutionData.market?.leadershipWalksPercentage
        ),
        sparcWalk: toPercent(lhExecutionData.market?.sparcWalksPercentage),
      },
      {
        region: lhExecutionData.region?.regionName || "Region",
        pulseCheck: toPercent(lhExecutionData.region?.pulseChecksPercentage),
        observation: toPercent(lhExecutionData.region?.observationsPercentage),
        shift: toPercent(lhExecutionData.region?.shiftHandoversPercentage),
        leadershipWalk: toPercent(
          lhExecutionData.region?.leadershipWalksPercentage
        ),
        sparcWalk: toPercent(lhExecutionData.region?.sparcWalksPercentage),
      },
    ];
  }, [lhExecutionData, error?.lhExecution]);

  // Loading state component
  const LoadingState = () => (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        marginTop: 12,
        height: 80,
      }}
    >
      <ActivityIndicator size="large" color={colors.brand.primary} />
      <Text style={[themeStyles.loadingText, { marginLeft: 8, fontSize: 14 }]}> 
        {TEXT.METRICS.LOADING}
      </Text>
    </View>
  );

  // Arrow indicator component
  const ArrowIndicator = () => (
    <TouchableOpacity
      style={{
        alignSelf: "center",
        marginTop: 8,
        padding: 8,
      }}
      onPress={() => setMetricsOverlayVisible(true)}
    >
      <View
        style={{
          width: 0,
          height: 0,
          backgroundColor: "transparent",
          borderStyle: "solid",
          borderLeftWidth: 8,
          borderRightWidth: 8,
          borderTopWidth: 12,
          borderLeftColor: "transparent",
          borderRightColor: "transparent",
          borderTopColor: "#666",
        }}
      />
    </TouchableOpacity>
  );

  // Metrics cards grid component
  const MetricsCards = () => (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {formattedMetrics &&
        Object.entries(formattedMetrics).map(([key, item]) => (
          <MetricCard
            key={key}
            name={item.name}
            value={item.value}
            onPress={() => setMetricsOverlayVisible(true)}
          />
        ))}
    </View>
  );

  // Error state component
  const ErrorState = () => {
    // Check for timeout error in the error message (504)
    const isTimeoutError =
      error?.lhExecution?.includes("504") ||
      error?.lhExecution?.includes("timeout") ||
      error?.lhExecution?.includes("timed out");

    return (
      <View
        style={{
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          marginTop: 12,
          height: 80,
        }}
      >
          <Text style={[themeStyles.errorText, { fontSize: 14 }]}> 
          {isTimeoutError
            ? "Unable to load the metrics, request timed out"
            : "No metrics data available for this location"}
        </Text>
      </View>
    );
  };

  // Function to render metrics content
  const renderMetricsContent = () => {
    if (loading.lhExecution) {
      return <LoadingState />;
    }

    // Show error state when there's an API error
    if (error?.lhExecution) {
      return <ErrorState />;
    }

    return (
      <View>
        <MetricsChart 
          data={metricsChartData} 
          height={300} 
          width={screenWidth - 32}
          isLoading={loading.lhExecution}
        />
      </View>
    );
  };

  // Handler for timeframe dropdown change
  const handleTimeFrameChange = useCallback(
    (val: any) => {
      if (val && !Array.isArray(val)) {
        const newTimeFrame = val.id as string;

        // Get the cache key for this timeframe
        const locationId = selectedLocation?.id;
        if (!locationId) {
          return;
        }

        const cacheKey = `${locationId}:${newTimeFrame}`;

        // Check if we're trying to select the same timeframe that previously had an error
        const hasErrorForSelectedTimeFrame =
          error?.lhExecution && newTimeFrame === lastLoadedTimeFrame;

        // Only proceed with timeframe change and data fetch if:
        // 1. It's different from current timeframe OR
        // 2. We're selecting a previously failed timeframe but want to retry
        if (
          newTimeFrame !== selectedTimeFrame ||
          hasErrorForSelectedTimeFrame
        ) {
          // When explicitly selecting a timeframe in the dropdown (even if previously failed),
          // we should allow a retry by clearing the error state
          if (
            hasErrorForSelectedTimeFrame ||
            failedTimeframesRef.current[cacheKey]
          ) {
            console.log(
              `User initiated retry for previously failed timeframe: ${newTimeFrame}`
            );
            // Clear error state
            dispatch(setLHExecutionError(null));
            // Clear failed state to allow retry
            delete failedTimeframesRef.current[cacheKey];
          }

          setSelectedTimeFrame(newTimeFrame);

          // Only set dirty flag if:
          // 1. New timeframe is different from last loaded OR
          // 2. We want to retry a previously failed request
          if (
            newTimeFrame !== lastLoadedTimeFrame ||
            hasErrorForSelectedTimeFrame
          ) {
            setMetricsDirty(true);
          } else {
            setMetricsDirty(false);
          }
        }
      }
    },
    [
      setSelectedTimeFrame,
      selectedTimeFrame,
      lastLoadedTimeFrame,
      setMetricsDirty,
      error,
      dispatch,
      selectedLocation,
    ]
  );

  // No local overlay state needed - using parent's state management

  // Determine device type
  const isIphone = Platform.OS === 'ios' && screenWidth < 768;

  return (
    <View style={[lightStyles.topLeftCard, themeStyles.cardSurface]}>
      {/* Header Section with Title, Dropdown, and View Details */}
      <View style={{ 
        flexDirection: isIphone ? 'column' : 'row', 
        justifyContent: 'space-between', 
        alignItems: isIphone ? 'flex-start' : 'center', 
        marginBottom: 12,
        gap: isIphone ? 8 : 0,
      }}>
        {/* Title */}
        <Text style={[lightStyles.sectionHeader, themeStyles.sectionHeader]}>
          {TEXT.HEADERS.LIGHTHOUSE_EXECUTION}
        </Text>

        {/* Dropdown and View Details on Right (or second row on iPhone) */}
        <View style={{ 
          flexDirection: 'row', 
          alignItems: 'center', 
          justifyContent: isIphone ? 'space-between' : 'flex-end',
          gap: isIphone ? 0 : 40,
          width: isIphone ? '100%' : 'auto',
        }}>
          {/* Timeframe Dropdown */}
          <View
            style={[
              lightStyles.dropdownContainer,
              { width: 140, borderRadius: 8 },
            ]}
          >
            <CrossPlatformDropdown
              testID="timeframe-dropdown"
              childList={TIME_FRAME_OPTIONS}
              selectedValue={
                TIME_FRAME_OPTIONS.find(
                  (option) => option.id === selectedTimeFrame
                ) || null
              }
              setSelectedValue={handleTimeFrameChange}
              size="small"
              readOnly={loading.lhExecution}
              disableSorting={true}
              disabledBackgroundColor={colors.background.surface}
            />
          </View>

          {/* View Details Link */}
          <Text 
            style={themeStyles.viewDetailsText}
            onPress={() => setMetricsOverlayVisible(true)}
          >
            View details
          </Text>
        </View>
      </View>

      {/* Chart Section */}
      <View style={lightStyles.metricsFiltersContainer}>
        {/* Render metrics content or loading state */}
        {renderMetricsContent()}
      </View>
    </View>
  );
};

export default MetricsSection;
